import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import type { TooltipPayload } from 'recharts';
import { Area, ComposedChart, Label, Line, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from '../util/classes.ts';

const currentColor = 'var(--color-blue-500)';
const previousColor = 'var(--color-slate-300)';

function activeDot(fill: string): { r: number; fill: string; stroke: string; strokeWidth: number } {
    return {
        r: 3,
        fill,
        stroke: 'var(--color-container)',
        strokeWidth: 1,
    };
}

function seriesValue(payload: TooltipPayload, dataKey: string): number | undefined {
    const value = payload.find((entry) => entry.dataKey === dataKey)?.value;
    return typeof value === 'number' ? value : undefined;
}

export type TrendChartPoint = {
    /** X-axis category. Expected to be unique — it also anchors the crosshair. */
    label: string;
    current?: number;
    previous?: number;
};

export type TrendChartProps = ComponentPropsWithRef<'div'> & {
    data: readonly TrendChartPoint[];
    currentLabel?: string;
    previousLabel?: string;
    formatValue?: (value: number) => string;
};

/**
 * Year-over-year trend chart: the comparison series as a bare line, the current
 * (possibly partial) series as a filled area. Hover, tap or drag to scrub — a
 * crosshair snaps to the nearest point and a readout shows both values. Arrow
 * keys walk the series for keyboard users.
 *
 * The chart owns no title, card or legend chrome — compose it with
 * `TrendChartLegend` and size it with `className` (e.g. `h-28`).
 */
export function TrendChart({
    data,
    currentLabel,
    previousLabel,
    formatValue = (value: number): string => value.toLocaleString(),
    className,
    ref,
    ...restProps
}: TrendChartProps): ReactElement {
    const rootRef = useRef<HTMLDivElement>(null);
    const mergedRef = useMergedRefs(ref, rootRef);
    // recharts forwards this ref to its `.recharts-wrapper` div — the node that carries the
    // mouse handlers — even though its types name it `SVGSVGElement`.
    const wrapperRef = useRef<SVGSVGElement>(null);
    const gradientId = `trend-chart-fill-${useId().replaceAll(':', '')}`;

    const values = data.flatMap((point) => [point.current, point.previous]).filter((v) => v !== undefined);
    const peak = values.length > 0 ? Math.max(...values) : undefined;

    // Make touch mirror desktop hover: the readout is live only while a finger is down and
    // dragging. recharts scrubs on touch-move but never clears on touch-end (a finger fires
    // no mouse-leave), so fake the mouse-leave on touch-end to drop the readout when the finger lifts.
    useEffect(() => {
        const root = rootRef.current;

        if (!root) {
            return;
        }

        const scheduleClear = (): void => {
            requestAnimationFrame(() => {
                wrapperRef.current?.dispatchEvent(
                    new MouseEvent('mouseout', { bubbles: true, relatedTarget: document.body }),
                );
            });
        };

        root.addEventListener('touchend', scheduleClear);
        root.addEventListener('touchcancel', scheduleClear);

        return () => {
            root.removeEventListener('touchend', scheduleClear);
            root.removeEventListener('touchcancel', scheduleClear);
        };
    }, []);

    return (
        <div
            ref={mergedRef}
            data-slot="trend-chart"
            data-base-ui-swipe-ignore=""
            className={cn(
                'w-full touch-none select-none [&_svg]:overflow-visible',
                '[-webkit-tap-highlight-color:transparent]',
                '[&_*:focus]:outline-none',
                '[&_.recharts-surface:focus-visible]:outline-1 [&_.recharts-surface:focus-visible]:outline-(--color-main) [&_.recharts-surface:focus-visible]:outline-offset-1',
                className,
            )}
            {...restProps}
        >
            <ComposedChart
                ref={wrapperRef}
                width="100%"
                height="100%"
                data={data}
                margin={{ top: 6, right: 0, bottom: 4, left: 0 }}
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={currentColor} stopOpacity={0.18} />
                        <stop offset="100%" stopColor={currentColor} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <XAxis dataKey="label" hide />
                <YAxis hide domain={[0, (dataMax: number) => dataMax * 1.08]} />
                {peak !== undefined && (
                    <ReferenceLine y={peak} stroke="var(--color-line-weak)" strokeDasharray="2 3">
                        {/* The peak value replaces a y-axis: one label, right-aligned above the line. */}
                        <Label
                            value={formatValue(peak)}
                            position="insideTopRight"
                            offset={2}
                            dy={-17}
                            className="fill-content-weaker text-ui-sm"
                        />
                    </ReferenceLine>
                )}
                {/* Fill only — the current stroke is drawn as a Line below so it
                    stacks on top of `previous`; recharts paints Lines over Areas
                    regardless of order, which would otherwise hide `current` where
                    the two series coincide (e.g. both zero). */}
                <Area
                    dataKey="current"
                    stroke="none"
                    fill={`url(#${gradientId})`}
                    dot={false}
                    activeDot={false}
                    type="monotone"
                    isAnimationActive={false}
                />
                <Line
                    dataKey="previous"
                    stroke={previousColor}
                    strokeWidth={1.25}
                    dot={false}
                    activeDot={activeDot('var(--color-slate-400)')}
                    type="monotone"
                    isAnimationActive={false}
                />
                <Line
                    dataKey="current"
                    stroke={currentColor}
                    strokeWidth={1.5}
                    dot={false}
                    activeDot={activeDot(currentColor)}
                    type="monotone"
                    isAnimationActive={false}
                />
                <Tooltip
                    cursor={{ stroke: 'var(--color-line-strong)', strokeDasharray: '2 2' }}
                    position={{ y: 0 }}
                    isAnimationActive={false}
                    content={({ active, label, payload }): ReactNode => {
                        if (!active || payload.length === 0) {
                            return null;
                        }
                        const current = seriesValue(payload, 'current');
                        const previous = seriesValue(payload, 'previous');
                        return (
                            <div className="whitespace-nowrap rounded-md border border-line-weak bg-container px-cozy py-1.5 text-ui-sm shadow-md">
                                <div className="font-medium text-content">{label}</div>
                                <div className="mt-1 flex flex-col gap-0.5">
                                    <div className="flex items-center gap-cozy">
                                        <span
                                            className="size-1.5 shrink-0 rounded-full"
                                            style={{ background: currentColor }}
                                        />
                                        {currentLabel !== undefined && (
                                            <span className="text-content-weaker">{currentLabel}</span>
                                        )}
                                        <span
                                            className={cn(
                                                'tabular-nums text-content',
                                                currentLabel !== undefined && 'ml-auto pl-default',
                                            )}
                                        >
                                            {current === undefined ? '–' : formatValue(current)}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-cozy">
                                        <span
                                            className="size-1.5 shrink-0 rounded-full"
                                            style={{ background: previousColor }}
                                        />
                                        {previousLabel !== undefined && (
                                            <span className="text-content-weaker">{previousLabel}</span>
                                        )}
                                        <span
                                            className={cn(
                                                'tabular-nums text-content',
                                                previousLabel !== undefined && 'ml-auto pl-default',
                                            )}
                                        >
                                            {previous === undefined ? '–' : formatValue(previous)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    }}
                />
            </ComposedChart>
        </div>
    );
}

export type TrendChartLegendProps = ComponentPropsWithRef<'div'> & {
    currentLabel: string;
    previousLabel: string;
    startLabel?: string;
    endLabel?: string;
};

/** The chart's caption row: the series swatches, plus the first/last x labels. */
export function TrendChartLegend({
    currentLabel,
    previousLabel,
    startLabel,
    endLabel,
    className,
    ...restProps
}: TrendChartLegendProps): ReactElement {
    return (
        <div
            data-slot="trend-chart-legend"
            className={cn('flex items-center justify-between text-ui-sm text-content-weaker', className)}
            {...restProps}
        >
            {startLabel !== undefined && <span>{startLabel}</span>}
            <div className="mx-auto flex items-center gap-default text-content-weak">
                <span className="flex items-center gap-tight">
                    <span className="size-1.5 rounded-full" style={{ background: currentColor }} />
                    {currentLabel}
                </span>
                <span className="flex items-center gap-tight">
                    <span className="size-1.5 rounded-full" style={{ background: previousColor }} />
                    {previousLabel}
                </span>
            </div>
            {endLabel !== undefined && <span>{endLabel}</span>}
        </div>
    );
}
